import React from 'react';
import { NamespaceProvider, NamespaceContext } from '@ux/namespace-component';
import AccountDelegation from '@ux/account-delegation';
import { events, Customer, Employee } from '@ux/header-util';
import NavigationBottom from './nav-bottom';
import NavigationTop from './nav-top';
import ApplicationFooter from '@ux/application-footer';
import AbstractHeader from '@ux/abstract-header';
import HeaderComponent from './header';
import assign from 'object-assign';
import PropTypes from 'prop-types';
import Cart from '@px/header-cart';
import { IntlProvider, FormattedMessage } from 'react-intl';
import Link from '@ux/link';
import XIcon from '@ux/icon/x';
import { breakpoints } from './enums';
import { cookie } from '@ux/util';

/**
 * Return a namespace if the environment variable is set, 'polyfilled' by WebPack.
 *
 * @returns {Object} Configured namespace.
 * @api private
 */
function namespace() {
  return {
    namespace: process.env.NAMESPACE // eslint-disable-line no-process-env
  };
}

/**
 * Render and expose the Header as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Header.
 * @api public
 */
class Header extends AbstractHeader {
  constructor(props) {
    super(...arguments);
    this.state = {
      dismissedMerchBanner: cookie.get('uxp-merch-banner-dismiss') === props.merchBannerFingerprint,
      CartComponent: Cart /* eslint-disable no-unused-vars */
    };
    this.updateCartComponent = NavigationTop.updateCartComponent.bind(this);
    this.updateNavigation = NavigationBottom.update.bind(this);
    this.updateTopNavigation = NavigationTop.update.bind(this);
    this.updateHelpUrl = NavigationTop.updateHelpUrl.bind(this);
    this.dismissMerchBanner = this.dismissMerchBanner.bind(this);
    this.renderMerchBanners = this.renderMerchBanners.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    this.breakpoint(...breakpoints);
    this.pipeExpVariantToState({
      expId: 'uxp_appheader_mobilesubnav',
      attributes: {
        market: this.props.market
      },
      stateFieldName: 'mobileSubNav'
    });
    this.setExpVariantOnState({
      experimentId: 'uxp_appheader_AA',
      stateFieldName: 'AA'
    });
  }

  /**
   * Handle a user clicking the X to permanently dismiss this
   * banner.
   *
   * @param {React.SyntheticEvent} evt Event
   */
  dismissMerchBanner(evt) {
    evt.preventDefault();
    const { merchBannerFingerprint } = this.props;

    cookie.setItem('uxp-merch-banner-dismiss', merchBannerFingerprint);

    this.setState({
      dismissedMerchBanner: true
    });
  }

  /**
   * Returns a valid Customer instance using the props associated
   * with this instance. If `internalMode` is `true` returns an `Employee`.
   *
   * @returns {User} Customer or Employee instance from @ux/header-util.
   */
  createCustomer() {
    const props = this.props;

    // Override customer if internal mode
    if (props.preset === 'internal-header') {
      return new Employee(props);
    }

    return new Customer(props);
  }

  /**
   * Prefer state over props when assigning to children
   *
   * @returns {Object} Child props
   * @api private
   */
  getChildProps() {
    const { helpUrl, breakpoint } = this.state;
    const childProps = assign({}, this.props, this.state);
    if (helpUrl) childProps.urls.help.href = helpUrl;
    return childProps;
  }

  /**
   * Merch Banners
   *
   * @param {childProps} childProps from children
   * @param {function} namespace from header
   * @returns {React.ReactElement} Merch Banner
   * @api private
   */
  renderMerchBanners(childProps, namespace) {
    const { breakpoint } = childProps;
    const isDesktop = !~['mobile', 'phablet', 'tablet'].indexOf(breakpoint);

    return (
      <div className={ namespace('merch-banner') }>
        <button onClick={ this.dismissMerchBanner } className={ namespace('banner-close') }>
          <XIcon width={ 24 } height={ 24 } />
        </button>
        <div className={ namespace('browsers-message') }>
          <span>
            <FormattedMessage id='PC:AppHeader:MerchBanners:Title' />.&nbsp;
          </span>
          <span>
            { isDesktop ? <FormattedMessage id='PC:AppHeader:MerchBanners:TextDesktop' /> : <FormattedMessage id='PC:AppHeader:MerchBanners:TextMobile' /> }
          </span>
        </div>
        <span className={ namespace('learn-more-btn') }>
          <Link
            className={ namespace('btn', 'btn-sm', 'btn-default-light-secondary') }
            urlArgs={ this.props.urlArgs }
            data-eid='uxp.hyd.merch_banner.learn_more_btn.click'
            href={ childProps.merchBanner.buttonHref }>
            <FormattedMessage id='PC:AppHeader:MerchBanners:ButtonText' />
          </Link>
        </span>
      </div>
    );
  }

  render() {
    const childProps = this.getChildProps();
    const { features } = childProps;
    const isReseller = childProps.privateLabelId !== 1;
    const internalApp = childProps.preset === 'internal-header';
    const showMerchBanners = features && features.merchBanners && !internalApp && !isReseller && childProps.loggedIn;
    const { dismissedMerchBanner } = this.state;

    return (
      <IntlProvider locale={ childProps.market } messages={ childProps.messages }>
        <NamespaceProvider { ...namespace() }>
          <NamespaceContext.Consumer>
            { context =>
              <div className={ context.namespace(this.classNames({
                loaded: this.isBrowser
              })) }>
                { this.renderBanner(childProps) }
                { childProps.preset === 'internal-header' ? null : <AccountDelegation
                  market={ childProps.market }
                  messages={ childProps.messages }
                  action={ childProps.urls.sso.exitDelegation.href }
                  restoreCookie={ childProps.urls.sso.restoreCookie.href }
                  customer={ this.customer } /> }
                { !dismissedMerchBanner && showMerchBanners && this.renderMerchBanners(childProps, context.namespace) }
                <HeaderComponent { ...childProps } />
              </div>
            }
          </NamespaceContext.Consumer>
        </NamespaceProvider>
      </IntlProvider>
    );
  }
}

Header.propTypes = {
  market: PropTypes.string.isRequired,
  urls: PropTypes.object.isRequired,
  resellerUrls: PropTypes.object,
  privateLabelType: PropTypes.number,
  privateLabelId: PropTypes.number,
  preset: PropTypes.string,
  traffic: PropTypes.string
};

Header.defaultProps = {
  preset: 'application-header'
};

/**
 * Render and expose the Footer as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Footer.
 * @api public
 */
class Footer extends React.Component {
  componentDidMount() {
    events.emit('mount', 'footer', this);
    events.emit('mount:footer', this);
  }

  render() {
    return (
      <NamespaceProvider { ...namespace() }>
        <ApplicationFooter { ...this.props } />
      </NamespaceProvider>
    );
  }
}

export {
  Header,
  Footer
};
